import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { formatarDataCivilFinanceiro } from "../helpers/formatar-data-financeiro";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import type {
  CancelamentoTituloDialogState,
  CancelamentoTituloForm,
} from "../types/cancelamento-titulo.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import {
  CONTAS_FIN_ACCENT,
  fieldSx,
  modalContentSx,
  modalHeaderSx,
  modalPaperSx,
} from "../../components/contas-financeiras.theme";

export type CancelamentoTituloDialogProps = {
  dialog: CancelamentoTituloDialogState;
  form: CancelamentoTituloForm;
  setForm: Dispatch<SetStateAction<CancelamentoTituloForm>>;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

function labelVencimento(iso: string | null | undefined): string {
  return formatarDataCivilFinanceiro(iso);
}

function ResumoMetrica({ label, valor }: { label: string; valor: string }) {
  return (
    <Box flex={1} minWidth={0}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} noWrap>
        {valor}
      </Typography>
    </Box>
  );
}

export default function CancelamentoTituloDialog({
  dialog,
  form,
  setForm,
  loading,
  error,
  onClose,
  onConfirm,
}: CancelamentoTituloDialogProps) {
  const tipoLabel = dialog.tipo === "RECEBER" ? "Receber" : "Pagar";
  const tituloPrincipal =
    dialog.tituloDescricao?.trim() ||
    dialog.tituloDetalhe?.trim() ||
    "Título financeiro";

  const podeConfirmar = !loading && form.motivo.trim().length >= 5;

  const valorAbertoLabel =
    dialog.valorAberto != null && Number.isFinite(dialog.valorAberto)
      ? formatarMoedaBRL(dialog.valorAberto)
      : "—";

  const patch =
    (field: keyof CancelamentoTituloForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
    };

  return (
    <Dialog
      open={dialog.open}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="cancelamento-titulo-dialog-title"
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BlockOutlinedIcon sx={{ fontSize: 40, opacity: 0.9, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="cancelamento-titulo-dialog-title"
              variant="overline"
              sx={{ opacity: 0.75, letterSpacing: 1 }}
            >
              Cancelamento
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap title={tituloPrincipal}>
              {tipoLabel} — {tituloPrincipal}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mt: 0.25 }}>
              O título permanece no histórico, mas sai das previsões em aberto.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={loading}
            sx={{ color: "inherit" }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={modalContentSx}>
        <Stack spacing={2}>
          {error ? (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          ) : null}

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              bgcolor: "grey.50",
              borderLeft: `4px solid ${CONTAS_FIN_ACCENT}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {tituloPrincipal}
            </Typography>
            {dialog.tituloDetalhe ? (
              <Typography variant="body2" color="text.secondary">
                {dialog.tipo === "RECEBER" ? "Cliente" : "Fornecedor"}:{" "}
                {dialog.tituloDetalhe}
              </Typography>
            ) : null}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 1.5 }}
            >
              <ResumoMetrica label="Status atual" valor={dialog.status ?? "—"} />
              <ResumoMetrica label="Valor em aberto" valor={valorAbertoLabel} />
              <ResumoMetrica
                label="Vencimento"
                valor={labelVencimento(dialog.dataVencimento)}
              />
            </Stack>
          </Box>

          <Alert severity="warning" sx={{ py: 0.5 }}>
            Confirma o cancelamento deste título? Essa ação não exclui o registro.
          </Alert>

          <FormField
            label="Motivo do cancelamento"
            value={form.motivo}
            onChange={patch("motivo")}
            disabled={loading}
            required
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            helperText="Mínimo de 5 caracteres."
            placeholder="Descreva o motivo do cancelamento"
            sx={fieldSx}
          />

          <FormField
            label="Observação adicional (opcional)"
            value={form.observacao}
            onChange={patch("observacao")}
            disabled={loading}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <MDButton
          color="dark"
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={onClose}
        >
          Fechar
        </MDButton>
        <MDButton
          color="error"
          variant="gradient"
          size="small"
          disabled={!podeConfirmar}
          onClick={() => void onConfirm()}
        >
          {loading ? "Processando…" : "Confirmar cancelamento"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
